import { accSub } from '@/utils'

export const TYPE = {
    INTERSET: 'interest',
    PRINCIPAL: 'principal'
}

export const TYPES = [
    {
        type: 'interest',
        text: '等额本息'
    },
    {
        type: 'principal',
        text: '等额本金'
    }
]

export const defaultType = TYPES[0]

export const defaultResult = {
    monthly_payment: 0,
    monthly_decrease: 0,
    total_interest: 0,
    total_payment: 0
}

export const LOAN_TYPES = {
    BUSINESS: '商业贷款',
    PUBLIC_FUND: '公积金贷款',
    COMPOSE: '组合贷款'
}

export const CALCS = [
    {
        key: 'HOUSE_PRICE',
        value: '按房屋总价'
    },
    {
        key: 'TOTAL_LOANS',
        value: '按贷款总额'
    },
]

export const CALC_TYPES = {
    TOTAL_LOANS: '按贷款总额',
    HOUSE_PRICE: '按房屋总价'
}

export const defaultCalcType = CALC_TYPES.TOTAL_LOANS

export const defaultLoanType = LOAN_TYPES.BUSINESS

export const years = new Array(26).fill().map((v, i) => `${i + 5}年`)
export const defaultYear = parseInt(years[years.length - 1])

export const INTEREST_RATE_MAP = {
    [LOAN_TYPES.BUSINESS]: (years) => {
        const oneYearRate = '4.35'
        const fiveYearsRate = '4.75'
        const greaterFiveYearsRate = '4.90'
        let base = oneYearRate
        if (years > 1 && years <= 5) {
            base = fiveYearsRate
        } else if (years > 5) {
            base = greaterFiveYearsRate
        }
        return {
            tx: '基准利率',
            value: base
        }
    },
    [LOAN_TYPES.PUBLIC_FUND]: (years) => {
        const fiveYearsRate = '2.75'
        const greaterFiveYearsRate = '3.25'
        const base = years > 5 ? greaterFiveYearsRate : fiveYearsRate
        return {
            tx: '基准利率',
            value: base
        }
    }
}

export const formatNumber = (num) => {
    const initNum = Number(num).toFixed(2)
    const arr = initNum.split('.')
    const intPart = Number(arr[0]).toLocaleString()
    const decimalPart = arr[1]
    return intPart + '.' + decimalPart
}

/**
 * @param {贷款额度} baseLoan (万)
 * @param {贷款月数} month 
 * @param {月利率} monthRate 
 */
const getIntresetRs = (baseLoan = 0, month, monthRate) => {
    const loan = baseLoan * 10000
    const ratePowMonth = Math.pow((1 + monthRate), month)
    const monthPay = loan * monthRate * ratePowMonth / (ratePowMonth - 1)
    const totalPay = monthPay * month
    return {
        monthly_payment: monthPay, //每月还款
        total_interest: totalPay - loan, // 总利息
        total_payment: totalPay // 总还款
    }
}

const getMonthIntresetRs = (baseLoan, month, monthRate, monthNum, restPayment) => {
    const loan = baseLoan * 10000
    const { monthly_payment } = getIntresetRs(baseLoan, month, monthRate)
    const interest_payment = loan * monthRate * (Math.pow(1 + monthRate, month) - Math.pow(1 + monthRate, monthNum - 1)) /
        (Math.pow(1 + monthRate, month) - 1)
    const base_payment = monthly_payment - interest_payment
    const temp = accSub(restPayment, base_payment)
    const reset_payment = temp > 1 ? temp : 0
    return {
        monthly_payment,
        base_payment,
        interest_payment,
        reset_payment
    }
}

/**
 * @param {贷款额度} baseLoan (万)
 * @param {贷款月数} month 
 * @param {月利率} monthRate 
 */
const getPrincipalRs = (baseLoan = 0, month, monthRate) => {
    const loan = baseLoan * 10000
    const firstMonthPayment = loan / month + loan * monthRate
    const monthlyLoan = loan / month
    const monthlyDecrease = monthlyLoan * monthRate
    const totalInterest = ((monthlyLoan + loan * monthRate) + monthlyLoan * (1 + monthRate)) / 2 * month - loan
    return {
        monthly_payment: firstMonthPayment, // 每月还款（其实是首月还款）
        total_interest: totalInterest, // 总利息
        total_payment: totalInterest + loan, // 总还款
        monthly_decrease: monthlyDecrease // 每月递减
    }
}

const getMonthPublicRs = (baseLoan, month, monthRate, monthNum, restPayment) => {
    const loan = baseLoan * 10000
    const base_payment = loan / month
    const totalPaidBase = (monthNum - 1) * base_payment
    const interest_payment = (loan - totalPaidBase) * monthRate
    const monthly_payment = base_payment + interest_payment
    const temp = accSub(restPayment, base_payment)
    const reset_payment = temp > 1 ? temp : 0
    return {
        monthly_payment,
        base_payment,
        interest_payment,
        reset_payment
    }
}

const getMonthComposeRs = (option, month, monthNum, restPayment) => {
    const { type, business_loan, public_loan, business_loan_rate, public_loan_rate } = option
    const { public_rest, business_rest } = restPayment
    let business_rs
    let public_rs
    if (type.type === TYPE.INTERSET) {
        business_rs = getMonthIntresetRs(Number(business_loan), month, getMonthRate(business_loan_rate), monthNum, business_rest)
        public_rs = getMonthIntresetRs(Number(public_loan), month, getMonthRate(public_loan_rate), monthNum, public_rest)
    } else if (type.type === TYPE.PRINCIPAL) {
        business_rs = getMonthPublicRs(Number(business_loan), month, getMonthRate(business_loan_rate), monthNum, business_rest)
        public_rs = getMonthPublicRs(Number(public_loan), month, getMonthRate(public_loan_rate), monthNum, public_rest)
    }
    return {
        monthly_payment: business_rs.monthly_payment + public_rs.monthly_payment,
        base_payment: business_rs.base_payment + public_rs.base_payment,
        interest_payment: business_rs.interest_payment + public_rs.interest_payment,
        reset_payment: {
            business_rest: business_rs.reset_payment,
            public_rest: public_rs.reset_payment
        }
    }
}

const getRsMap = {
    [TYPE.INTERSET]: getIntresetRs,
    [TYPE.PRINCIPAL]: getPrincipalRs
}

const getMonthRate = yearRate => yearRate / 12 / 100

export const getResult = (option) => {
    const { loan, type } = option
    const month = option.year * 12
    const computer = getRsMap[type.type]
    if (option.loan_type === LOAN_TYPES.COMPOSE) {
        const businessRs = computer(option.business_loan, month, getMonthRate(option.business_loan_rate))
        const publicRs = computer(option.public_loan, month, getMonthRate(option.public_loan_rate))
        return {
            monthly_payment: businessRs.monthly_payment + publicRs.monthly_payment,
            total_interest: businessRs.total_interest + publicRs.total_interest,
            total_payment: businessRs.total_payment + publicRs.total_payment,
            monthly_decrease: businessRs.monthly_decrease + publicRs.monthly_decrease
        }
    }
    const monthRate = getMonthRate(option.loan_rate)
    return computer(loan, month, monthRate)
}

const getMonthDetail = (option, monthNum, restPayment) => {
    const { type, loan_type, loan, loan_rate, year } = option
    const month = year * 12
    if (loan_type === LOAN_TYPES.COMPOSE) {
        return getMonthComposeRs(option, month, monthNum, restPayment)
    }
    if (type.type === TYPE.INTERSET) {
        return getMonthIntresetRs(Number(loan), month, getMonthRate(loan_rate), monthNum, restPayment)
    }
    if (type.type === TYPE.PRINCIPAL) {
        return getMonthPublicRs(Number(loan), month, getMonthRate(loan_rate), monthNum, restPayment)
    }
}

export const getDetail = (option) => {
    let { from_date, year } = option
    const detail = []

    const date_arr = from_date.split('-')
    const from_year = Number(date_arr[0])
    const from_month = Number(date_arr[1])
    const total_month = Number(year) * 12
    let monthNum = 0
    let month = from_month
    let yearNum = from_year
    let restPayment = (option.loan_type === LOAN_TYPES.COMPOSE ? {
        business_rest: option.business_loan * 10000,
        public_rest: option.public_loan * 10000
    } : option.loan * 10000)
    while (monthNum < total_month) {
        let month_data_arr = []
        for (; month <= 12 && monthNum < total_month; month++) {
            monthNum += 1
            const data = getMonthDetail(option, monthNum, restPayment)
            const month_data = {
                month,
                data
            }
            // reset rest payment
            restPayment = data.reset_payment
            month_data_arr.push(month_data)
        }
        const year_detail = {
            year: yearNum,
            month_data_arr
        }
        year_detail.total_payment = month_data_arr.reduce((total, { data }) => {
            const { monthly_payment, interest_payment } = data
            total.total_pay += monthly_payment
            total.total_interest_pay += interest_payment
            return total
        }, { total_pay: 0, total_interest_pay: 0 })
        detail.push(year_detail)
        month = 1
        yearNum++
    }
    return detail
}
